import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { gql } from '@apollo/client'
import { AnimatePresence, motion } from 'framer-motion'
import { isMobile } from 'react-device-detect'
import { ReactRef } from 'use-callback-ref/dist/es5/types'
import { Button } from '@/atoms/Button'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { CaptionSM, HeadingXS, ParagraphMD } from '@/atoms/Text'
import { paths } from '@/constants'
import { Modal } from '@/molecules/Modal'
import { createBasicShareLink } from '@/services/Sharing'
import { useUser } from '@/services/UserService'
import { buildLoginUrl } from '@/utils/BuildLoginUrl'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'
import { CompletionStep } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/ShareGuildTicketsModal/CompletionStep'
import { ConfirmStep } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/ShareGuildTicketsModal/ConfirmationStep'
import { DefaultStep } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/ShareGuildTicketsModal/DefaultStep'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

const DONATE_GUILD_TICKETS_MUTATION = gql`
  mutation DiscountsDonateGuildToPif($input: DiscountsDonateGuildToPifInput!) {
    discountsDonateGuildToPif(input: $input) {
      quantityGifted
      error {
        description
        reference
      }
    }
  }
`

interface ShareGuildTicketsModalProps {
  open: boolean
  onClose: () => void
  projectName: string
  theatricalSlug: string
  modalRef?: ReactRef<HTMLDivElement>
  ModalStep?: ModalStep
}

type ModalStep = 'main' | 'gift-confirmation' | 'gift-completion'

export const ShareGuildTicketsModal = ({
  open,
  onClose,
  projectName,
  theatricalSlug,
  modalRef,
  ModalStep = 'main',
}: ShareGuildTicketsModalProps) => {
  const { t } = useTranslate('common')
  const { isLoggedIn } = useUser()
  const [isCopied, setIsCopied] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState<ModalStep>(ModalStep)
  const [codesFromUrl, setUrlCodes] = useState<string[]>([])
  const timeoutRef = useRef<NodeJS.Timeout>()
  const { discountCodes, giftGuildTicketsMobile, refetchGuildTickets } = useGuildTickets(theatricalSlug)

  const urlDiscountCodes = useCallback((searchParams: string): string[] => {
    const urlParams = new URLSearchParams(searchParams)
    const discountParam = urlParams.get('discounts')
    if (!discountParam) return []
    return discountParam.split(',').filter(Boolean)
  }, [])

  const handleModalClose = useCallback(() => {
    if (currentStep === 'gift-completion') {
      refetchGuildTickets().catch((err) => {
        logger().error('Failed to refetch guild tickets', {
          err,
          theatricalSlug,
        })
      })
    }
    onClose()
  }, [currentStep, onClose, refetchGuildTickets, theatricalSlug])

  useEffect(() => {
    if (open) {
      setCurrentStep(ModalStep)
    }
  }, [open, ModalStep])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        setIsCopied(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!discountCodes || discountCodes.length === 0) {
      setShareUrl(null)
      return
    }

    createBasicShareLink({
      baseUrl: `${paths.getBaseURL()}/tickets/${theatricalSlug}`,
      params: { flow: 'guild-gift', discounts: discountCodes.join(',') },
    }).then((url: string) => setShareUrl(url))
  }, [discountCodes, theatricalSlug])

  // Handle URL discount codes on client-side only
  useEffect(() => {
    if (typeof window !== 'undefined' && !isLoggedIn) {
      setUrlCodes(urlDiscountCodes(window.location.search))
    }
  }, [isLoggedIn, urlDiscountCodes])

  const onCopyClick = useCallback(async () => {
    if (!shareUrl) return

    if (isMobile && typeof navigator?.share === 'function') {
      await giftGuildTicketsMobile()
      return
    }

    if (navigator?.clipboard) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setIsCopied(true)
      })

      const timeout = setTimeout(() => {
        setIsCopied(false)
      }, 2000)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = timeout
    }
  }, [shareUrl, giftGuildTicketsMobile])

  const handleGiftClick = useCallback(() => {
    setCurrentStep('gift-confirmation')
  }, [])

  const handleGoBack = useCallback(() => {
    setCurrentStep('main')
  }, [])

  const handleLogin = useCallback(() => {
    const currentUrl = window.location.href
    window.location.href = buildLoginUrl(currentUrl)
  }, [])

  const [donateGuildTickets, { loading: isDonating }] = useMutation(DONATE_GUILD_TICKETS_MUTATION)

  const handleGiftConfirm = useCallback(async () => {
    const effectiveDiscountCodes = isLoggedIn ? discountCodes : codesFromUrl

    if (!effectiveDiscountCodes || effectiveDiscountCodes.length === 0) return

    try {
      const input = isLoggedIn
        ? {
            quantity: effectiveDiscountCodes.length,
            theatricalSlug,
          }
        : {
            codes: effectiveDiscountCodes,
          }

      const result = await donateGuildTickets({
        variables: {
          input: input,
        },
      })

      const { error } = result.data?.discountsDonateGuildToPif || {}

      if (error) {
        logger().error('Donating Guild Tickets Failed', {
          error,
          theatricalSlug,
        })
        return
      }

      setCurrentStep('gift-completion')
    } catch (err) {
      logger().error('Donating Guild Tickets Failed', {
        err,
        theatricalSlug,
      })
    }
  }, [discountCodes, codesFromUrl, donateGuildTickets, theatricalSlug, isLoggedIn])

  const renderStepContent = () => {
    // Use the actual guild tickets for logged-in users
    const effectiveDiscountCodes = isLoggedIn ? discountCodes : codesFromUrl

    // If not logged in, and no codes from url,show login required message
    if (!isLoggedIn && !codesFromUrl.length) {
      return (
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="mx-auto rounded-full bg-warning-50 p-[1.125rem]">
            <TicketIcon size={32} color="warm-03" />
          </div>
          <HeadingXS weight="bold" className="text-[1.25rem] leading-[2rem] -tracking-[0.03125rem] md:text-[1.75rem]">
            {t('loginRequired', 'Login Required')}
          </HeadingXS>
          <ParagraphMD weight="medium" color="core-gray-700">
            {t('pleaseLoginToShareTickets', 'Please log in to share your Guild tickets.')}
          </ParagraphMD>
          <Button
            variant="black"
            onClick={handleLogin}
            className="min-w-[9.375rem] items-center gap-1.5 rounded-[0.875rem] px-5 py-3 text-sm font-normal"
          >
            {t('login', 'Log In')}
          </Button>
        </div>
      )
    }

    const commonProps = {
      onClose: handleModalClose,
      ticketQuantity: effectiveDiscountCodes.length,
      projectName,
    }
    switch (currentStep) {
      case 'gift-confirmation':
        return (
          <ConfirmStep
            {...commonProps}
            handleGiftConfirm={handleGiftConfirm}
            handleGoBack={handleGoBack}
            isLoading={isDonating}
          />
        )
      case 'gift-completion':
        return <CompletionStep {...commonProps} />
      default:
        return <DefaultStep onClose={onClose} onCopyClick={onCopyClick} handleGiftClick={handleGiftClick} />
    }
  }

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={handleModalClose}
      backgroundOverlayClassName="!bg-black/85"
      panelClasses="relative shadow-dark-5 rounded-3xl  !w-[calc(100%-2em)] !max-w-[31.25rem] !p-0 relative text-center"
      transition="appear"
      className="z-[99999]"
    >
      <div ref={modalRef} className="h-full w-full min-w-[22.1875rem] !p-6 !pt-8 md:!p-10 md:!pt-12">
        {renderStepContent()}
        <AnimatePresence>
          {isCopied && (
            <motion.div
              className="absolute inset-x-0 bottom-3.5"
              transition={{
                y: { type: 'spring', bounce: 0.3 },
                opacity: { duration: 0.3 },
                ease: 'easeInOut',
              }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
            >
              <CaptionSM color="core-gray-700">{t('urlCopiedToClipBoard', 'URL copied to clipboard')}</CaptionSM>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Modal>
  )
}
